import { type SectionVisibilityDefaultsRequest } from '@aysar/contracts';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { Callout } from '@/shared/components/feedback/Callout';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Button } from '@/shared/components/ui/Button/Button';

import { useSectionVisibilityDefaultsModalLogic } from './useSectionVisibilityDefaultsModalLogic';

/**
 * Visibility defaults for every question of one section.
 *
 * Presentation only; the count, the confirmation, the write and the honest
 * result all live in the hook (U5).
 *
 * The scope callout is not decoration: this dialog is opened from the questions
 * pane, which is a **page** of a **filtered** list, and the write is neither. It
 * sits in the kit's slot between the choices and the buttons — the last thing
 * read before Apply — and the confirmation then says it again with the same
 * number.
 */
export function SectionVisibilityDefaultsModal({ open, onCancel, modalData }: ModalComponentProps) {
  const sectionId = typeof modalData?.['sectionId'] === 'string' ? modalData['sectionId'] : '';
  const view = useSectionVisibilityDefaultsModalLogic({ sectionId, onDone: onCancel });

  return (
    <DynamicFormModal<SectionVisibilityDefaultsRequest>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={view.title}
      size="lg"
      // ONE column, like the editor's own visibility section: the form holds a
      // single field that lays itself out as a 2×2, and the kit's four-column
      // default gave it one track of the dialog and left the rest empty — the
      // "a region with nothing in it must not reserve its track" defect P10.6d
      // recorded, arriving one dialog later.
      columns={1}
      isLoading={view.isLoading}
      fields={view.fields}
      schema={view.schema}
      initialValues={view.initialValues}
      submitLabel={view.submitLabel}
      onCancel={onCancel}
      onSubmit={view.submit}
    >
      <Callout
        // `danger` when the count could not be read: the notice has stopped
        // describing the write and started refusing it.
        tone={view.isCountUnknown ? 'danger' : 'warning'}
        liveness={view.isCountUnknown ? 'alert' : 'status'}
        action={
          view.isCountUnknown ? (
            <Button type="button" variant="outline" size="sm" onClick={view.retryCount}>
              {view.retryLabel}
            </Button>
          ) : undefined
        }
      >
        {view.scope}
      </Callout>
    </DynamicFormModal>
  );
}
